import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import waClient from '../utils/waproxy-calls';
import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import { waSessionStorage } from '../utils/session-storage.js';
import encryptedLocalStorage from '../utils/encrypted-local-storage.js';
import { initializeCircuitBreakers, stopAllCircuitBreakers } from '../utils/circuit-breaker.js';
import { updateApiKey } from '../utils/routing-config-manager.js';

// Create context with default values
export const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  worldAnvilKey: '',
  username: '',
  loading: true,
  validateWorldAnvilKey: () => {},
  logout: () => {},
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

// Provider component to wrap your app
export const AuthProvider = ({ children }) => {
  // State management with localStorage persistence for non-sensitive data
  const [user, setUser] = useLocalStorage('wa_user', null);
  const [username, setUsername] = useLocalStorage('wa_username', '');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // For the API key, we don't use localStorage anymore - it's handled by secure storage
  const [worldAnvilKey, setWorldAnvilKey] = useState('');
  
  // Sync worldAnvilKey state with unified storage
  useEffect(() => {
    const syncWAApiKey = async () => {
      const secureKey = await waSessionStorage.getWAApiKey();
      if (secureKey !== worldAnvilKey) {
        setWorldAnvilKey(secureKey || '');
      }
    };
    syncWAApiKey();
  }, []);  // Retiré worldAnvilKey de la dépendance pour éviter les boucles de mise à jour
  
  // Function to validate World Anvil API key - defined first to avoid reference issues
  const validateWorldAnvilKey = useCallback(async (key, username = '') => {
    setLoading(true);
    
    try {
      // Stocker la clé dans le stockage de session avant de faire l'appel
      // Cela garantit que la clé est disponible pour l'authentification
      await waSessionStorage.setWAApiKey(key);
      
      // Mettre à jour la clé API dans la configuration de routage AVANT l'authentification
      await updateApiKey('wa', key);
      debug.log(config, 'Clé API WA stockée dans routing_config');

      // réaliser la validation (utiliser directement la clé fournie)
      const response = await waClient.authenticate(key);

      if (response.success) {
        const userData = response.user;
        
        // Vérifier si le nom d'utilisateur saisi correspond à celui retourné par l'API (insensible à la casse)
        if (username && userData && userData.username) {
          if (username.toLowerCase() !== userData.username.toLowerCase()) {
            return { 
              success: false, 
              error: 'Le nom d\'utilisateur ne correspond pas à votre compte World Anvil',
              details: 'Veuillez saisir le même nom d\'utilisateur que celui de votre compte World Anvil'
            };
          }
        }
        
        setUser(userData);
        setWorldAnvilKey(key);
        setUsername(userData?.username || '');
        setIsAuthenticated(true);
        
        // Initialiser les circuit breakers après un login réussi
        debug.log(config, 'Initialisation des circuit breakers après authentification réussie');
        initializeCircuitBreakers();
        
        return { success: true, user: userData };
      } else {
        setUser(null);
        setWorldAnvilKey('');
        setUsername('');
        setIsAuthenticated(false);
        
        // Supprimer la clé en cas d'échec
        waSessionStorage.removeWAApiKey();
        
        return { 
          success: false, 
          error: response.error || 'Échec de validation de la clé World Anvil',
          details: response.details || ''
        };
      }
    } catch (error) {
      setUser(null);
      setWorldAnvilKey('');
      setUsername('');
      setIsAuthenticated(false);
      
      // Supprimer la clé en cas d'erreur
      waSessionStorage.removeWAApiKey();
      
      return { 
        success: false, 
        error: error.message || 'Une erreur est survenue',
        details: error.stack || ''
      };
    } finally {
      setLoading(false);
    }
  }, [setUser, setWorldAnvilKey, setUsername]);
  
  // Update authentication state when dependencies change
  useEffect(() => {
    const newAuthState = !!user && !!worldAnvilKey;
    setIsAuthenticated(newAuthState);
    // Logging pour débogage
    debug.log(config, 'Auth state updated:', { 
      newAuthState, 
      hasUser: !!user, 
      hasKey: !!worldAnvilKey, 
      currentAuthState: isAuthenticated 
    });
  }, [user, worldAnvilKey]);
  
  // Auto-validate saved key on component mount - uniquement lors du premier rendu
  useEffect(() => {
    // Ajoutons un flag pour s'assurer que cette validation n'est exécutée qu'une fois
    const autoValidateKey = async () => {
      // Récupérer la clé depuis le stockage unifié
      const storedKey = await waSessionStorage.getWAApiKey();
      
      if (storedKey) {
        debug.log(config, 'Auto-validation de la clé WA_XAuthToken depuis le stockage unifié');
        setWorldAnvilKey(storedKey);
        
        if (!user) {
          // Pas d'utilisateur en mémoire, re-valider la clé
          await validateWorldAnvilKey(storedKey);
        } else {
          try {
            // On marque comme authentifié directement pour éviter les re-rendus
            setIsAuthenticated(true);
            // Vérification silencieuse en arrière-plan sans mise à jour d'état en cas d'échec
            // réaliser la validation avec la clé stockée
            const result = await waClient.authenticate(storedKey);
            if (!result.success) {
              debug.warn(config, 'Stored API key is no longer valid');
              setUser(null);
              setWorldAnvilKey('');
              setIsAuthenticated(false);
            } else {
              // Synchroniser l'utilisateur avec les données les plus récentes
              setUser(result.user);
              
              // Initialiser les circuit breakers pour les reconnexions automatiques
              debug.log(config, 'Initialisation des circuit breakers après reconnexion automatique');
              initializeCircuitBreakers();
            }
          } catch (error) {
            debug.error(config, 'Auto-validation failed:', error);
          }
        }
      } else if (user) {
        setUser(null);
      }
      
      // Always mark as not loading at the end
      setLoading(false);
    };
    
    autoValidateKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Logout function
  const logout = useCallback(async () => {
    waClient.logout();
    setUser(null);
    setWorldAnvilKey('');
    setUsername('');
    setIsAuthenticated(false);
    
    // Arrêter les circuit breakers lors de la déconnexion
    debug.log(config, 'Arrêt des circuit breakers lors de la déconnexion');
    stopAllCircuitBreakers();
    
    // Supprimer la clé des deux systèmes de stockage
    waSessionStorage.removeWAApiKey();
    // encryptedLocalStorage.removeItem('wa_api_key');
  }, [setUser, setUsername]);
  
  // Prepare context value
  const contextValue = {
    user,
    isAuthenticated,
    worldAnvilKey,
    username,
    loading,
    validateWorldAnvilKey,
    logout
  };
  
  // Render provider
  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};